import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Linkedin, ArrowRight, MessageCircle } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Tende da Sole', path: '/tende-da-sole' },
    { name: 'Tende da Interno', path: '/tende-da-interno' },
    { name: 'Pergotende', path: '/pergotende' },
    { name: 'Bioclimatiche', path: '/bioclimatiche' },
    { name: 'Vetrate Panoramiche', path: '/vetrate-panoramiche' },
  ];

  const companyLinks = [
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Installazioni', path: '/installazioni' },
    { name: 'Shop Online', path: '/shop' },
    { name: 'Richiedi Preventivo', path: '/preventivo' },
  ];

  const areaLinks = [
    { name: 'Pisa', path: '/aree-servite/pisa' },
    { name: 'Lucca', path: '/aree-servite/lucca' },
    { name: 'Livorno', path: '/aree-servite/livorno' },
    { name: 'Firenze', path: '/aree-servite/firenze' },
  ];

  return (
    <footer className="bg-gray-950 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img 
                src="/assets/logo-bianco.png" 
                alt="Giovanni Tende Logo" 
                className="h-10 w-auto"
              />
              <span className="text-2xl font-light tracking-wider">
                <span className="font-semibold">Giovanni</span> Tende
              </span>
            </Link>
            <p className="text-gray-400 font-light leading-relaxed mb-2">
              Produttori diretti dal 1967
            </p>
            <p className="text-gray-500 text-sm font-light leading-relaxed mb-6">
              Gli unici produttori di tende da sole in Toscana. Sartoria e laboratorio proprio.
            </p>
            
            {/* Partner Tempotest */}
            <div className="mb-6 p-3 bg-white/5 rounded-lg inline-block">
              <p className="text-xs text-gray-500 mb-2">Partner Ufficiale</p>
              <img 
                src="/assets/tempotest-logo.png" 
                alt="Tempotest by Parà - Italian Performance Fabrics"
                className="h-16 w-auto"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center text-gray-400 hover:border-sky-500 hover:text-sky-500 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={companyInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center text-gray-400 hover:border-sky-500 hover:text-sky-500 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={`https://wa.me/39${companyInfo.mobile.replace(/\s/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-500 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-6 text-white">
              Soluzioni
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 font-light hover:text-sky-400 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-6 text-white">
              Azienda
            </h4>
            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 font-light hover:text-sky-400 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-6 text-white">
              Contatti
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 font-light">
                  {companyInfo.address.street}<br />
                  {companyInfo.address.cap} {companyInfo.address.city} ({companyInfo.address.province})
                </span>
              </li>
              <li>
                <a
                  href={`tel:+39${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-gray-400 font-light hover:text-sky-400 transition-colors duration-300"
                >
                  <Phone className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  {companyInfo.phone} (Fisso)
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/39${companyInfo.mobile.replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 font-light hover:text-green-400 transition-colors duration-300"
                >
                  <MessageCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  {companyInfo.mobile} (WhatsApp)
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 font-light hover:text-sky-400 transition-colors duration-300"
                >
                  <Mail className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                <div className="text-gray-400 font-light text-sm">
                  <p>{companyInfo.workingHours.weekdays}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm font-light">
              © {currentYear} Tappezzeria Giovanni. Tutti i diritti riservati. | P.IVA {companyInfo.vatNumber}
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-gray-300 transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/cookie" className="hover:text-gray-300 transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
